import React, { useState, useRef, useEffect } from 'react';
import { format } from 'date-fns';
import { Check, CheckCheck, AlertCircle, Brain, ChevronDown, ChevronUp } from 'lucide-react';
import { Button, Alert } from './index';
import Spinner from './Spinner';
import './MessageList.css';

const MessageList = ({ 
  messages = [], 
  isLoading = false,
  loadingMessage = "AI is thinking...",
  showTimestamps = true,
  showStatus = true,
  showReasoning = true,
  className = '',
  onMessageClick = null,
  onReasoningToggle = null
}) => {
  const [expandedReasoning, setExpandedReasoning] = useState({});
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleReasoningToggle = (messageId) => {
    setExpandedReasoning(prev => ({
      ...prev,
      [messageId]: !prev[messageId]
    }));
    onReasoningToggle?.(messageId, !expandedReasoning[messageId]);
  };

  const parseAIMessage = (content) => {
    const scratchpadMatch = content.match(/<scratchpad>(.*?)<\/scratchpad>/s);
    const userResponseMatch = content.match(/<user_response>(.*?)<\/user_response>/s);
    
    const reasoning = scratchpadMatch ? scratchpadMatch[1].trim() : null;
    const response = userResponseMatch ? userResponseMatch[1].trim() : content;
    
    return { reasoning, response, hasReasoning: !!reasoning };
  };

  const formatTime = (timestamp) => {
    try {
      return format(new Date(timestamp), 'HH:mm');
    } catch (error) {
      return '--:--';
    }
  };

  const getMessageStatus = (message) => {
    const isUser = message.role === 'user' || message.type === 'user';
    if (isUser && showStatus) {
      if (message.isError) {
        return <AlertCircle className="h-4 w-4 text-red-500" />;
      }
      return <CheckCheck className="h-4 w-4 text-blue-500" />;
    }
    return null;
  };

  const getBubbleClasses = (message) => {
    const isUser = message.role === 'user' || message.type === 'user';
    const isAI = message.role === 'assistant' || message.type === 'ai';
    const isSystem = message.role === 'system' || message.type === 'system';

    if (isUser) {
      return 'bg-indigo-600 text-white ml-auto';
    } else if (isAI) {
      return 'bg-gray-100 text-gray-900';
    } else if (isSystem) {
      return message.isError 
        ? 'bg-red-50 text-red-700 border border-red-200' 
        : 'bg-yellow-50 text-yellow-700 border border-yellow-200';
    }
    return 'bg-gray-100 text-gray-900';
  };

  const getAvatar = (message) => {
    const isUser = message.role === 'user' || message.type === 'user';
    const isAI = message.role === 'assistant' || message.type === 'ai';
    const isSystem = message.role === 'system' || message.type === 'system';

    if (isUser) {
      return (
        <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center text-white text-sm font-medium">
          U
        </div>
      );
    } else if (isAI) {
      return (
        <div className="w-8 h-8 bg-green-600 rounded-full flex items-center justify-center text-white">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
          </svg>
        </div>
      );
    } else if (isSystem) {
      return (
        <div className="w-8 h-8 bg-gray-400 rounded-full flex items-center justify-center text-white">
          <AlertCircle className="w-4 h-4" />
        </div>
      );
    }
    return null;
  };

  const renderMessage = (message, index) => {
    const isUser = message.role === 'user' || message.type === 'user';
    const isAI = message.role === 'assistant' || message.type === 'ai';
    const messageId = message.id || index;
    const isExpanded = expandedReasoning[messageId];

    const { reasoning, response, hasReasoning } = isAI ? parseAIMessage(message.content) : { 
      reasoning: null, 
      response: message.content, 
      hasReasoning: false 
    };

    return (
      <div 
        key={messageId}
        className={`message-item ${onMessageClick ? 'cursor-pointer' : ''}`}
        onClick={() => onMessageClick?.(message)}
      >
        <div className={`flex items-end space-x-3 ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
          {/* AI Avatar - only for AI messages */}
          {!isUser && (
            <div className="flex-shrink-0">
              {getAvatar(message)}
            </div>
          )}
          
          {/* User Avatar - only for user messages (placed first so it appears on right with flex-row-reverse) */}
          {isUser && (
            <div className="flex-shrink-0">
              {getAvatar(message)}
            </div>
          )}
          
          {/* Message Bubble */}
          <div className={`flex flex-col max-w-xs md:max-w-lg lg:max-w-2xl ${isUser ? 'items-end' : 'items-start'}`}>
            <div className={`px-4 py-3 rounded-2xl ${getBubbleClasses(message)}`}>
              <p className="text-sm md:text-base whitespace-pre-wrap break-words leading-relaxed">
                {response}
              </p>
              
              {/* Reasoning Toggle Button for AI messages */}
              {isAI && hasReasoning && showReasoning && (
                <Button
                  onClick={() => handleReasoningToggle(messageId)}
                  variant="ghost"
                  size="small"
                  className="mt-2 text-xs text-gray-500 hover:text-gray-700"
                >
                  <Brain className="h-3 w-3 mr-1" />
                  <span>Thought process</span>
                  {isExpanded ? (
                    <ChevronUp className="h-3 w-3 ml-1" />
                  ) : (
                    <ChevronDown className="h-3 w-3 ml-1" />
                  )}
                </Button>
              )}
              
              {/* Reasoning Content */}
              {isAI && isExpanded && hasReasoning && (
                <Alert variant="info" className="mt-3">
                  <div className="flex items-center space-x-1 mb-2">
                    <Brain className="h-3 w-3 text-blue-600" />
                    <span className="text-xs font-medium text-blue-700">AI Reasoning</span>
                  </div>
                  <p className="text-xs text-blue-800 whitespace-pre-wrap leading-relaxed">
                    {reasoning}
                  </p>
                </Alert>
              )}
            </div>
            
            {/* Message Footer */}
            {showTimestamps && (
              <div className={`flex items-center space-x-1 mt-2 text-xs text-gray-500 ${isUser ? 'flex-row-reverse space-x-reverse' : ''}`}>
                <span>{formatTime(message.timestamp)}</span>
                {getMessageStatus(message)}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (messages.length === 0 && !isLoading) {
    return (
      <div className={`message-list-empty ${className}`}>
        <div className="empty-state">
          <div className="empty-icon">💬</div>
          <h3 className="empty-title">No messages yet</h3>
          <p className="empty-description">Start a conversation by sending a message</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`message-list ${className}`}>
      {messages.map((message, index) => renderMessage(message, index))}
      
      {/* Loading Indicator */}
      {isLoading && (
        <div className="message-loading">
          <div className="flex items-center space-x-2">
            <Spinner size="small" />
            <span className="text-sm text-gray-500">{loadingMessage}</span>
          </div>
        </div>
      )}
      
      <div ref={messagesEndRef} />
    </div>
  );
};

export default MessageList;
